import { ladderAccess, settingsSections, useSettingsSections } from '@church/data'
import { SETTINGS_TABS as TABS } from '../lib/settings-tabs.ts'
import { Link, Outlet, createFileRoute, redirect, useLocation } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { Icon, PageHeader } from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'

/**
 * Settings → Organization, as a tabbed layout. Each section shows to whoever
 * its page serves (`settingsSections`): the church's settings to an
 * Organization Viewer, the directory's to a People Administrator, group
 * types to a Groups Administrator. The server re-checks each call — this
 * only spares people a wall of 403s.
 */
export const Route = createFileRoute('/_authed/_shell/settings')({
  beforeLoad: ({ context }) => {
    // Somewhere in Settings for them, or not Settings at all.
    if (!Object.values(settingsSections(ladderAccess(context.me))).some(Boolean)) {
      throw redirect({ to: '/' })
    }
  },
  component: SettingsLayout,
})

function SettingsLayout() {
  const nav = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const sections = useSettingsSections()
  const tabs = TABS.filter((tab) => sections[tab.section])
  const section = TABS.find((tab) => pathname === tab.to || pathname.startsWith(`${tab.to}/`))
  useBreadcrumbs(section ? [{ label: section.label }] : [])
  // On a phone the sections are one sideways row: keep the open one in view.
  useEffect(() => {
    nav.current
      ?.querySelector('[data-status="active"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [pathname])

  return (
    <div className="space-y-6 lg:flex lg:items-start lg:gap-8 lg:space-y-0">
      <PageHeader title="Organization settings" />

      {/* A phone scrolls the sections sideways, edge to edge; a wide screen
          lists them down the side, so none hides on a second row. */}
      <div className="-mx-gutter border-b border-border-subtle sm:mx-0 lg:sticky lg:top-20 lg:w-56 lg:shrink-0 lg:border-b-0">
        <nav
          ref={nav}
          aria-label="Settings sections"
          className="no-scrollbar flex gap-1 overflow-x-auto px-gutter sm:px-0 lg:flex-col lg:overflow-visible"
        >
          {tabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              // `data-status` rather than activeProps: two border colours in one
              // class list leave the winner to stylesheet order, not to us.
              className="flex min-h-11 shrink-0 items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm font-medium whitespace-nowrap text-content-muted transition-colors duration-(--duration-fast) hover:text-content-default data-[status=active]:border-brand-solid data-[status=active]:text-content-brand sm:px-4 lg:rounded-control lg:border-b-0 lg:px-3 lg:hover:bg-surface-sunken lg:data-[status=active]:bg-brand-soft"
            >
              <Icon icon={tab.icon} className="size-3.5" />
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="min-w-0 lg:flex-1">
        <Outlet />
      </div>
    </div>
  )
}
